import React, { useCallback, useEffect, useState } from 'react';
import { Landmark, Loader2, RefreshCw, ShieldCheck, AlertTriangle } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import api from '../../api/client';

const KYC_BADGE = {
  approved: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30',
  pending: 'bg-amber-500/15 text-amber-300 border-amber-500/30',
  in_review: 'bg-amber-500/15 text-amber-300 border-amber-500/30',
  past_due: 'bg-red-500/15 text-red-300 border-red-500/30',
  restricted: 'bg-red-500/15 text-red-300 border-red-500/30',
};

const SUBJECT_LABEL = {
  individual: 'Persona fisica',
  sole_proprietor: 'Libero prof.',
  company: 'Società',
  organization: 'Associazione',
};

/** NeoUI — read-only overview of creator fiscal/billing data (onboarding).
 *  Clicking a row opens the existing CreatorBillingProfileDialog. */
const AdminCreatorFiscalPanel = ({ onOpenBilling }) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/admin/creators/billing-profiles');
      setRows(res.data.profiles || []);
    } catch (_e) {
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = rows.filter((r) => {
    if (!q.trim()) return true;
    const s = q.trim().toLowerCase();
    return (
      (r.username || '').toLowerCase().includes(s) ||
      (r.email || '').toLowerCase().includes(s) ||
      String(r.user_number || '').includes(s) ||
      (r.codice_fiscale || '').toLowerCase().includes(s) ||
      (r.vat_number || '').toLowerCase().includes(s)
    );
  });

  return (
    <div className="rounded-xl border border-border bg-card/40 p-4" data-testid="admin-fiscal-panel">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
        <h2 className="flex items-center gap-2 text-sm font-semibold">
          <Landmark className="h-4 w-4 gold-text" /> Dati fiscali Voce Pro (sola lettura)
        </h2>
        <div className="flex items-center gap-2">
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Cerca username, email, CF, P.IVA…"
            className="h-8 w-56 text-xs"
            data-testid="admin-fiscal-search"
          />
          <Button variant="ghost" size="sm" onClick={load} disabled={loading} data-testid="admin-fiscal-refresh">
            <RefreshCw className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} />
          </Button>
        </div>
      </div>
      <p className="mb-3 text-[11px] text-muted-foreground">
        Vista di sola lettura dei profili di fatturazione delle Voci Pro (onboarding). Clicca «Dettagli» per il profilo Stripe/fiscale completo.
      </p>
      {loading ? (
        <div className="flex justify-center py-10"><Loader2 className="h-5 w-5 animate-spin" /></div>
      ) : filtered.length === 0 ? (
        <p className="py-8 text-center text-xs text-muted-foreground" data-testid="admin-fiscal-empty">
          Nessun profilo fiscale trovato.
        </p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-left text-xs">
            <thead>
              <tr className="border-b border-border text-[10px] uppercase tracking-wider text-muted-foreground">
                <th className="py-2 pr-3">Voce Pro</th>
                <th className="py-2 pr-3">Tipo</th>
                <th className="py-2 pr-3">Paese</th>
                <th className="py-2 pr-3">Codice Fiscale</th>
                <th className="py-2 pr-3">P.IVA</th>
                <th className="py-2 pr-3">KYC</th>
                <th className="py-2 pr-3">Payout</th>
                <th className="py-2" />
              </tr>
            </thead>
            <tbody>
              {filtered.map((r) => (
                <tr key={r.user_id} className="border-b border-border/40 hover:bg-white/[0.02]" data-testid={`admin-fiscal-row-${r.username}`}>
                  <td className="py-2 pr-3">
                    <span className="font-semibold">@{r.username}</span>
                    {r.user_number && <span className="ml-1.5 text-muted-foreground">#{r.user_number}</span>}
                    <p className="text-[10px] text-muted-foreground">{r.email}</p>
                  </td>
                  <td className="py-2 pr-3">{SUBJECT_LABEL[r.subject_type] || r.subject_type || '—'}</td>
                  <td className="py-2 pr-3 uppercase">{r.fiscal_country || '—'}</td>
                  <td className="py-2 pr-3 font-mono text-[11px]">{r.codice_fiscale || '—'}</td>
                  <td className="py-2 pr-3 font-mono text-[11px]">{r.vat_number || '—'}</td>
                  <td className="py-2 pr-3">
                    <span className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-semibold ${KYC_BADGE[r.kyc_status] || 'bg-white/[0.05] text-muted-foreground border-border'}`}>
                      {r.kyc_status === 'approved' ? <ShieldCheck className="h-3 w-3" /> : <AlertTriangle className="h-3 w-3" />}
                      {r.kyc_status || 'unknown'}
                    </span>
                  </td>
                  <td className="py-2 pr-3">{r.is_payout_ready ? '✅' : '—'}</td>
                  <td className="py-2 text-right">
                    <Button
                      variant="outline"
                      size="sm"
                      className="h-7 text-[11px]"
                      onClick={() => onOpenBilling?.(r.user_id)}
                      data-testid={`admin-fiscal-detail-${r.username}`}
                    >
                      Dettagli
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default AdminCreatorFiscalPanel;
